import React, { ComponentType } from "react";

import { useFirstActionTracking } from "hooks";

interface FirstActionTrackerProps {
  children: React.ReactNode;
  actionName?: string;
  context?: Record<string, any>;
}

const FirstActionTracker: React.FC<FirstActionTrackerProps> = ({
  children,
  actionName = "page_load",
  context = {}
}) => {
  const { trackFirstAction } = useFirstActionTracking();

  const handleClick = (event: React.MouseEvent) => {
    trackFirstAction("click", `${actionName}_click`, {
      targetElement: (event.target as HTMLElement)?.tagName?.toLowerCase(),
      targetId: (event.target as HTMLElement)?.id,
      targetClassName: (event.target as HTMLElement)?.className,
      ...context
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      trackFirstAction("keyboard", `${actionName}_keyboard`, {
        key: event.key,
        targetElement: (event.target as HTMLElement)?.tagName?.toLowerCase(),
        ...context
      });
    }
  };

  const handleTouchStart = () => {
    trackFirstAction("touch", `${actionName}_touch`, context);
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
    >
      {children}
    </div>
  );
};

// Higher-order component for wrapping existing components
export function withFirstActionTracking<P extends object>(
  WrappedComponent: ComponentType<P>,
  actionName?: string,
  trackOnMount?: boolean,
  context?: Record<string, any>
) {
  const ComponentWithFirstActionTracking = (props: P) => (
    <FirstActionTracker actionName={actionName} context={context}>
      <WrappedComponent {...props} />
    </FirstActionTracker>
  );

  ComponentWithFirstActionTracking.displayName = `withFirstActionTracking(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithFirstActionTracking;
}

export default FirstActionTracker;
